import type { ReactNode } from "react";
import { createContext, useContext, useEffect, useState } from "react";
import { isFunction, isUndefined, noop } from "lodash";

export interface ExpandableBlockDataProviderProps {
  children: ReactNode;
  isDefaultOpen?: boolean;
  isOpen?: boolean;
  onToggle?: () => void;
  shouldCloseOnClickOutside?: boolean;
}

export interface ExpandableBlockDataProviderState {
  isDefaultOpen?: boolean;
  isOpen?: boolean;
  onToggle?: () => void;
  shouldCloseOnClickOutside?: boolean;
}

const ExpandableBlockData = createContext<ExpandableBlockDataProviderState>({
  isOpen: false,
  isDefaultOpen: false,
  shouldCloseOnClickOutside: false,
  onToggle: noop,
});
ExpandableBlockData.displayName = "ExpandableBlockData";

export const useExpandableBlockData = () => useContext(ExpandableBlockData);

export const ExpandableBlockDataProvider = ({
  isOpen: isOpenProp,
  isDefaultOpen,
  onToggle,
  shouldCloseOnClickOutside,
  children,
}: ExpandableBlockDataProviderProps) => {
  const [isOpen, setIsOpen] = useState(isDefaultOpen);
  const isControlled = !isUndefined(isOpenProp);

  useEffect(() => {
    if (!isControlled) {
      return;
    }
    setIsOpen(isOpenProp);
  }, [isOpenProp, isControlled]);

  const onToggleHandler = () => {
    if (isFunction(onToggle)) onToggle();
    if (isControlled) {
      return;
    }
    setIsOpen(prev => !prev);
  };

  return (
    <ExpandableBlockData.Provider
      value={{
        isOpen: isOpen ?? false,
        isDefaultOpen: isDefaultOpen ?? false,
        onToggle: onToggleHandler,
        shouldCloseOnClickOutside,
      }}
    >
      {children}
    </ExpandableBlockData.Provider>
  );
};
